/**
 * Reading and writing the local copies of what the household has.
 */

import type { Grocery, InventoryItem, Location, ShoppingEntry } from '../api/types';
import type { Database } from './database';

export interface Snapshot {
  items: InventoryItem[];
  locations: Location[];
  groceries: Grocery[];
  shopping: ShoppingEntry[];
}

export async function items(db: Database): Promise<InventoryItem[]> {
  return (await db.getAll('items')).map(readable);
}

export async function item(db: Database, id: string): Promise<InventoryItem | undefined> {
  const held = await db.get('items', id);

  return held === undefined ? undefined : readable(held);
}

/**
 * An item as this version of the app reads it, whichever version wrote it.
 *
 * Only one thing needs translating so far. `some` was the second of four rungs until the
 * ladder was cut to three, and a phone that has not re-read the household since is still
 * holding records that say it — which would draw a level button with nothing on it and cycle
 * to somewhere nobody asked for. It reads as `low`, which is what the tap that produced it
 * meant: one below plenty. The server's own table does the same for the same records, in
 * `level_from_code`.
 */
function readable(item: InventoryItem): InventoryItem {
  const retired: Record<string, InventoryItem['level']> = { some: 'low' };
  const level = retired[item.level];

  return level === undefined ? item : { ...item, level };
}

export function locations(db: Database): Promise<Location[]> {
  return db.getAll('locations');
}

export function groceries(db: Database): Promise<Grocery[]> {
  return db.getAll('groceries');
}

export function shoppingList(db: Database): Promise<ShoppingEntry[]> {
  return db.getAll('shopping');
}

export async function saveItem(db: Database, value: InventoryItem): Promise<void> {
  await db.put('items', value);
}

export async function saveLocation(db: Database, value: Location): Promise<void> {
  await db.put('locations', value);
}

export async function saveGrocery(db: Database, value: Grocery): Promise<void> {
  await db.put('groceries', value);
}

export async function forgetGrocery(db: Database, slug: string): Promise<void> {
  await db.delete('groceries', slug);
}

export async function saveShoppingEntry(db: Database, value: ShoppingEntry): Promise<void> {
  await db.put('shopping', value);
}

/**
 * Applies a change to the local copy of an item, if it is here.
 *
 * Returns what the item now looks like, so the caller can redraw without reading it back.
 * An item this device has never seen is not an error: the change came from the log, and the
 * refresh that follows will bring the item itself.
 */
export async function reviseItem(
  db: Database,
  id: string,
  revision: Partial<InventoryItem>,
): Promise<InventoryItem | undefined> {
  const existing = await item(db, id);
  if (existing === undefined) {
    return undefined;
  }

  const revised = { ...existing, ...revision };
  await saveItem(db, revised);

  return revised;
}

/**
 * Replaces everything with a fresh copy from the server.
 *
 * Only safe with an empty outbox: anything still queued has been applied locally but not yet
 * sent, and the server's copy does not know about it. [`SyncEngine`] enforces the ordering.
 */
export async function replaceEverything(db: Database, snapshot: Snapshot): Promise<void> {
  const transaction = db.transaction(
    ['items', 'locations', 'groceries', 'shopping'],
    'readwrite',
  );

  await Promise.all([
    transaction.objectStore('items').clear(),
    transaction.objectStore('locations').clear(),
    transaction.objectStore('groceries').clear(),
    transaction.objectStore('shopping').clear(),
  ]);

  await Promise.all([
    ...snapshot.items.map((value) => transaction.objectStore('items').put(value)),
    ...snapshot.locations.map((value) => transaction.objectStore('locations').put(value)),
    ...snapshot.groceries.map((value) => transaction.objectStore('groceries').put(value)),
    ...snapshot.shopping.map((value) => transaction.objectStore('shopping').put(value)),
  ]);

  await transaction.done;
}
